import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export interface RepoComboOption {
  value: string
  label: string
}

interface Props {
  id: string
  label: string
  placeholder: string
  value: string
  onChange: (value: string) => void
  options: RepoComboOption[]
  isLoading: boolean
  loadingText: string
  emptyText: string
  onQueryChange: (query: string) => void
  onOpenChange: (open: boolean) => void
  disabled?: boolean
  testId?: string
  labelClassName?: string
}

const DEFAULT_LABEL_CLASS = 'mb-1 block text-[10px] font-semibold uppercase tracking-wider text-foreground'

export function RepoSearchCombo({
  id, label, placeholder, value, onChange, options, isLoading,
  loadingText, emptyText, onQueryChange, onOpenChange, disabled, testId,
  labelClassName = DEFAULT_LABEL_CLASS,
}: Readonly<Props>) {
  const [input, setInput] = useState(value)
  const [open, setOpen] = useState(false)

  useEffect(() => { setInput(value) }, [value])
  useEffect(() => {
    const t = setTimeout(() => onQueryChange(input.trim()), 300)
    return () => clearTimeout(t)
  }, [input, onQueryChange])

  const setOpenState = (next: boolean) => { setOpen(next); onOpenChange(next) }

  const commit = (v: string) => {
    setInput(v)
    onChange(v)
    setOpenState(false)
  }

  return (
    <div className="relative">
      <Label htmlFor={id} className={labelClassName}>{label}</Label>
      <Input
        id={id}
        className="h-8 text-sm"
        placeholder={placeholder}
        value={input}
        autoComplete="off"
        disabled={disabled}
        data-testid={testId}
        onFocus={() => setOpenState(true)}
        onChange={e => { setInput(e.target.value); setOpenState(true) }}
        onBlur={() => { onChange(input.trim()); setTimeout(() => setOpenState(false), 150) }}
      />
      {open && !disabled && (
        <div
          className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-popover shadow-md"
          role="listbox"
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> {loadingText}</div>
          ) : options.length === 0 ? (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">{emptyText}</div>
          ) : (
            options.map(o => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                className="w-full cursor-pointer px-2 py-1.5 text-left text-sm hover:bg-accent"
                onMouseDown={e => { e.preventDefault(); commit(o.value) }}
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
